import { useEffect, useMemo, useRef, useState } from 'react';
import { CheckCircle, Info, Warning, X } from '@phosphor-icons/react';
import { BounceLoader } from 'react-spinners';
import colors from '@Common/assets/colors';
import { z, ZodRawShape } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useAuth } from '@Context/CognitoAuthProvider';
import { accountsAPI } from '@Lib/axios/account';
import { Creds } from '@Models/types';

interface Props {
  missing: Array<'aha_api_key' | 'testrail_username' | 'testrail_api_key'>;
  ahaOnly?: boolean;
}

const NewUserSettings: React.FC<Props> = ({ missing, ahaOnly }) => {
  const isAhaMissing = missing.includes('aha_api_key');
  const isTestRailMissing =
    missing.includes('testrail_username') ||
    missing.includes('testrail_api_key');
  const isAllMissing = isAhaMissing && isTestRailMissing;

  const schema = useMemo(() => {
    const shape: ZodRawShape = {};
    if (missing.includes('aha_api_key')) {
      shape.ahaKey = z.string().min(1, 'Aha! API Key is required');
    }
    if (missing.includes('testrail_username')) {
      shape.testrail_username = z
        .string()
        .min(1, 'Testrail Username is required');
    }
    if (missing.includes('testrail_api_key')) {
      shape.testrail_api_key = z
        .string()
        .min(1, 'Testrail API Key is required');
    }
    return z.object(shape);
  }, [missing]);

  const {
    register,
    handleSubmit,
    getValues,
    formState: { errors },
  } = useForm({ resolver: zodResolver(schema) });

  const [verificationState, setVerificationState] = useState({
    aha: '',
    testrail: '',
  });

  const { user, initializeUser, logout } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);

  //checks if value includes bullet characters - fix insertNewCred 500 status
  const isMasked = (val?: string) => val?.includes('\u2022');

  const hasAha = Boolean(user?.profile?.aha_api_key?.trim());

  const hasTestRail =
    Boolean(user?.profile?.testrail_username?.trim()) &&
    Boolean(user?.profile?.testrail_api_key?.trim());

  const account = accountsAPI(logout);

  const handleVerifyIntegrity = async (field: 'aha' | 'testrail') => {
    setIsVerifying(true);
    try {
      if (field === 'aha') {
        await account.validateAha(getValues('ahaKey'));
      } else {
        await account.validateTestRail(
          getValues('testrail_username'),
          getValues('testrail_api_key')
        );
      }
      setVerificationState((prev) => ({ ...prev, [field]: 'success' }));
    } catch {
      setVerificationState((prev) => ({ ...prev, [field]: 'failed' }));
    } finally {
      setIsVerifying(false);
    }
  };

  const [showInstructionModal, setShowInstructionModal] =
    useState<boolean>(false);
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && showInstructionModal) {
        setShowInstructionModal(false);
      }
    };

    if (showInstructionModal) {
      document.addEventListener('keydown', handleEscape);
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [showInstructionModal]);

  const onSubmit = async () => {
    const isAhaVerified = verificationState.aha === 'success';
    const isTestRailVerified = verificationState.testrail === 'success';
    const values = getValues();

    const shouldSubmit =
      (!isAhaMissing || isAhaVerified) &&
      (!isTestRailMissing || isTestRailVerified);

    if (shouldSubmit) {
      setIsLoading(true);

      const payload: Partial<Creds> = {
        email: String(localStorage.getItem('email')),
        name: user?.profile?.name || '',
      };

      //build the rest of the fields except masked fields (they already exist in the DB)
      if (isAhaVerified && !isMasked(values.ahaKey)) {
        payload.aha_api_key = values.ahaKey;
      }
      if (!isMasked(values.testrail_username)) {
        payload.testrail_username = values.testrail_username;
      }
      if (isTestRailVerified && !isMasked(values.testrail_api_key)) {
        payload.testrail_api_key = values.testrail_api_key;
      }

      try {
        await account.newAccountCredentials(payload as Creds);
        await initializeUser(true);
      } catch (error) {
        console.error(error);
      } finally {
        setIsLoading(false);
      }
    } else {
      if (isAhaMissing && verificationState.aha === 'failed') {
        setVerificationState((prev) => ({ ...prev, aha: '' }));
        await handleVerifyIntegrity('aha');
      }

      if (isTestRailMissing && verificationState.testrail === 'failed') {
        setVerificationState((prev) => ({ ...prev, testrail: '' }));
        await handleVerifyIntegrity('testrail');
      }

      if (
        isAhaMissing &&
        verificationState.aha !== 'success' &&
        verificationState.aha !== 'failed'
      ) {
        setVerificationState((prev) => ({ ...prev, aha: '' }));
        await handleVerifyIntegrity('aha');
      }

      if (
        isTestRailMissing &&
        verificationState.testrail !== 'success' &&
        verificationState.testrail !== 'failed'
      ) {
        setVerificationState((prev) => ({ ...prev, testrail: '' }));
        await handleVerifyIntegrity('testrail');
      }
    }
  };

  const renderButtonText = () => {
    if (isLoading) {
      return (
        <div className="flex flex-row items-center gap-x-2">
          <BounceLoader size={20} color={colors.brandWarning[500]} />
          <label className="animate pulse">Final Setup</label>
        </div>
      );
    }

    if (isVerifying) {
      return (
        <div className="flex flex-row items-center gap-x-2">
          <BounceLoader size={20} color={colors.brandWarning[500]} />
          <label className="animate-pulse">Verifying</label>
        </div>
      );
    }

    const isAhaVerified = verificationState.aha === 'success';
    const isTestRailVerified = verificationState.testrail === 'success';

    const shouldSubmit =
      (!isAhaMissing || isAhaVerified) &&
      (!isTestRailMissing || isTestRailVerified);

    return shouldSubmit ? 'Continue' : 'Verify';
  };

  return (
    <div className="main left-0 top-0 flex h-full w-full flex-col items-center justify-center rounded-lg">
      {isLoading}

      <div className="rounded-xl bg-[#fbfcff]">
        <div className="mx-8 mt-8 flex flex-col pl-2">
          <label className="text-xl font-bold text-gray-950">
            {isAllMissing && 'Aha! and TestRail Credentials Required'}
            {!isAllMissing && isAhaMissing && 'Aha! API Key Required'}
            {!isAllMissing &&
              isTestRailMissing &&
              'TestRail Credentials Required'}
          </label>
          <label className="text-xs font-medium text-gray-400">
            Please set your designated credentials below to continue.
          </label>
        </div>

        <div>
          <div className="mx-8 mb-8 mt-4 flex min-w-fit flex-col rounded-xl border border-gray-400 bg-white p-6">
            <div className="text-md mb-4 mr-8 flex w-auto min-w-[280px] flex-col pr-4 font-semibold text-gray-800">
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setShowInstructionModal(true)}
                  className="rounded-full p-1 text-blue-600 hover:bg-blue-100 hover:text-blue-800"
                  title="View setup instructions"
                  data-testid="api-keys-info-button"
                >
                  <Info size={20} weight="bold" />
                </button>
                <label>Manage API Keys</label>
              </div>

              <label className="text-xs text-gray-400">
                Here you can change your API Keys and testrail username. These
                settings are associated with your account.
              </label>
            </div>

            <form
              onSubmit={handleSubmit(onSubmit)}
              className="flex w-full flex-col rounded-lg border border-gray-300 bg-[#fbfcff] p-4"
            >
              <div className="flex flex-col text-sm font-medium text-gray-700">
                <div className="flex flex-row rounded p-1 px-2 hover:bg-[#edf7ff]">
                  <label className="flex w-[220px] py-1"> Aha! API Key</label>
                  <div className="relative flex flex-grow">
                    <input
                      {...register('ahaKey')}
                      data-testid="aha-key"
                      type="password"
                      defaultValue={user?.profile?.aha_api_key}
                      placeholder="Aha! API Key"
                      disabled={verificationState.aha === 'success' || hasAha}
                      className={`min-w-[350px] flex-grow rounded border p-2 py-1 font-normal text-gray-600 outline-none ${
                        verificationState.aha === 'success'
                          ? 'cursor-not-allowed border-green-400'
                          : verificationState.aha === 'failed' || errors.ahaKey
                            ? 'border-red-500'
                            : 'border-gray-300'
                      }`}
                    />
                  </div>
                </div>

                <div className="flex w-full flex-row justify-end">
                  {verificationState.aha === 'success' && (
                    <div className="flex flex-row items-center gap-x-2 text-xs text-green-500">
                      <CheckCircle size={20} className="inline" />
                      <span>Valid Aha! API Key</span>
                    </div>
                  )}

                  {verificationState.aha === 'failed' && (
                    <div className="flex flex-row items-center gap-x-2 text-xs text-red-500">
                      <Warning size={20} className="inline" />
                      <span>Invalid Aha! API Key</span>
                    </div>
                  )}

                  {errors.ahaKey && (
                    <div className="flex flex-row items-center gap-x-2 text-xs text-red-500">
                      <Warning size={20} className="inline" />
                      <span>{errors.ahaKey.message as string}</span>
                    </div>
                  )}
                </div>

                {!ahaOnly && (
                  <>
                    <div className="divider mx-1.5 my-4 border"></div>
                  </>
                )}
                {!ahaOnly && (
                  <>
                    <div className="flex flex-row rounded p-1 px-2 hover:bg-[#edf7ff]">
                      <label className="flex w-[220px] py-1">
                        Testrail Credentials
                      </label>
                      <div className="relative flex flex-grow">
                        <input
                          data-testid="testrail-username"
                          {...register('testrail_username')}
                          defaultValue={user?.profile?.testrail_username}
                          type="text"
                          disabled={
                            verificationState.testrail === 'success' ||
                            hasTestRail
                          }
                          placeholder="Testrail Username"
                          className={`flex-grow rounded border p-2 py-1 ${
                            verificationState.testrail === 'success'
                              ? 'cursor-not-allowed border-green-400'
                              : verificationState.testrail === 'failed' ||
                                  errors.testrail_username
                                ? 'border-red-500'
                                : 'border-gray-300'
                          }`}
                        />
                      </div>
                    </div>

                    <div className="flex flex-row rounded p-1 px-2 hover:bg-[#edf7ff]">
                      <label className="flex w-[220px] py-1"></label>
                      <div className="relative flex flex-grow">
                        <input
                          data-testid="testrail-key"
                          type="password"
                          defaultValue={user?.profile?.testrail_api_key}
                          placeholder="Testrail API Key"
                          {...register('testrail_api_key')}
                          disabled={
                            verificationState.testrail === 'success' ||
                            hasTestRail
                          }
                          className={`flex-grow rounded border p-2 py-1 ${
                            verificationState.testrail === 'success'
                              ? 'cursor-not-allowed border-green-400'
                              : verificationState.testrail === 'failed' ||
                                  errors.testrail_api_key
                                ? 'border-red-500'
                                : 'border-gray-300'
                          }`}
                        />
                      </div>
                    </div>

                    <div className="flex w-full flex-row justify-end">
                      {verificationState.testrail === 'success' && (
                        <div className="flex flex-row items-center gap-x-2 text-xs text-green-500">
                          <CheckCircle size={20} className="inline" />
                          <span>Valid TestRail Credentials</span>
                        </div>
                      )}

                      {verificationState.testrail === 'failed' && (
                        <div className="flex flex-row items-center gap-x-2 text-xs text-red-500">
                          <Warning size={20} className="inline" />
                          <span>Invalid TestRail Credentials</span>
                        </div>
                      )}

                      {(errors.testrail_username ||
                        errors.testrail_api_key) && (
                        <div className="flex flex-row items-center gap-x-2 text-xs text-red-500">
                          <Warning size={20} className="inline" />
                          <span>{'TestRail Credentials required'}</span>
                        </div>
                      )}
                    </div>
                  </>
                )}

                <div className="mx-2 mt-4 flex justify-end gap-2">
                  {isLoading || isVerifying ? (
                    renderButtonText()
                  ) : (
                    <button
                      data-testid="continue-btn"
                      type="submit"
                      className="flex h-8 w-32 items-center justify-center rounded bg-blue-800 px-6 py-3 text-white hover:bg-blue-800"
                    >
                      {renderButtonText()}
                    </button>
                  )}
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
      {showInstructionModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
          onClick={() => setShowInstructionModal(false)}
          role="dialog"
          aria-modal="true"
          aria-labelledby="modal-title"
        >
          <div
            className="relative max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-lg bg-white p-6 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-4 flex items-center justify-between">
              <h3
                id="modal-title"
                className="text-xl font-semibold text-gray-900"
              >
                API Keys Setup Instructions
              </h3>
              <button
                ref={closeButtonRef}
                onClick={() => setShowInstructionModal(false)}
                className="rounded-md p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                data-testid="close-instruction-modal"
                aria-label="Close instructions modal"
              >
                <X size={20} />
              </button>
            </div>

            <div className="space-y-6">
              {/* Aha! Instructions */}
              <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
                <h4 className="mb-3 flex items-center gap-2 text-lg font-semibold text-blue-900">
                  <Info size={20} weight="bold" />
                  Aha! API Key Setup
                </h4>
                <ol className="space-y-2 text-sm text-blue-800">
                  <li className="flex items-start gap-2">
                    <span className="flex h-6 w-6 items-center justify-center rounded-full bg-blue-600 text-xs font-bold text-white">
                      1
                    </span>
                    <span>
                      Click the user icon button on the top right section
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="flex h-6 w-6 items-center justify-center rounded-full bg-blue-600 text-xs font-bold text-white">
                      2
                    </span>
                    <span>Under settings, select Personal</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="flex h-6 w-6 items-center justify-center rounded-full bg-blue-600 text-xs font-bold text-white">
                      3
                    </span>
                    <span>Select Developer in the left side menu bar</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="flex h-6 w-6 items-center justify-center rounded-full bg-blue-600 text-xs font-bold text-white">
                      4
                    </span>
                    <span>Click the Generate API key button</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="flex h-6 w-6 items-center justify-center rounded-full bg-blue-600 text-xs font-bold text-white">
                      5
                    </span>
                    <span>Fill in the name textbox for the API key name</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="flex h-6 w-6 items-center justify-center rounded-full bg-blue-600 text-xs font-bold text-white">
                      6
                    </span>
                    <span>Click Generate API key button</span>
                  </li>
                </ol>
              </div>

              {/* TestRail Instructions */}
              <div className="rounded-lg border border-green-200 bg-green-50 p-4">
                <h4 className="mb-3 flex items-center gap-2 text-lg font-semibold text-green-900">
                  <Info size={20} weight="bold" />
                  TestRail API Key Setup
                </h4>
                <ol className="space-y-2 text-sm text-green-800">
                  <li className="flex items-start gap-2">
                    <span className="flex h-6 w-6 items-center justify-center rounded-full bg-green-600 text-xs font-bold text-white">
                      1
                    </span>
                    <span>Go to My Settings under the username dropdown</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="flex h-6 w-6 items-center justify-center rounded-full bg-green-600 text-xs font-bold text-white">
                      2
                    </span>
                    <span>Click the API Keys tab</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="flex h-6 w-6 items-center justify-center rounded-full bg-green-600 text-xs font-bold text-white">
                      3
                    </span>
                    <span>Click Add Key</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="flex h-6 w-6 items-center justify-center rounded-full bg-green-600 text-xs font-bold text-white">
                      4
                    </span>
                    <span>Input a name or title for the key</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="flex h-6 w-6 items-center justify-center rounded-full bg-green-600 text-xs font-bold text-white">
                      5
                    </span>
                    <span>Select Generate Key</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="flex h-6 w-6 items-center justify-center rounded-full bg-green-600 text-xs font-bold text-white">
                      6
                    </span>
                    <span>Click the Add Key button</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="flex h-6 w-6 items-center justify-center rounded-full bg-green-600 text-xs font-bold text-white">
                      7
                    </span>
                    <span>Click the Save Configuration button</span>
                  </li>
                </ol>
              </div>

              {/* Close button */}
              <div className="flex justify-end">
                <button
                  onClick={() => setShowInstructionModal(false)}
                  className="hover:bg-primary-800 rounded-lg bg-primary-700 px-4 py-2 text-white"
                  data-testid="close-instruction-modal-footer"
                >
                  Got it
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NewUserSettings;
